// Space bunnies levels. The id is fixed (progress is saved under it) – don't change it once the level is published.
import { defineLevels } from '../../core/registry';
import { on } from '../../core/level-helpers';
import type { StaticData } from '../../core/types';

const P = 16;   // the bunny's radius
const ground = (x: number, y: number, w: number, h?: number): StaticData => ({ x, y, w, h: h || 40, skin: 'space_rock' });
const metal = (x: number, y: number, w: number, h?: number, a?: number): StaticData => ({ x, y, w, h: h || 22, a: a || 0, skin: 'space_metal' });

export const levels = defineLevels([
  // 1 – Moon hop – on the moon the bunny falls slowly and flies far: one girder over the crater is enough
  (() => {
    const s1 = metal(230, 300, 380, 22, 12);
    return {
      id: 'moon-hop', par: 1, tools: { plank: 2 }, learn: ['gravity', 'ramp'], gravity: 0.45,
      statics: [s1, ground(210, 700, 420, 200), ground(560, 780, 280, 40), ground(950, 700, 500, 200)],
      balls: [on(s1, -160, P)],
      goal: { type: 'bowl', x: 900, y: 600, w: 200 },
      objs: [{ t: 'duck', x: 640, y: 330 }],
      solution: [{ t: 'plank', x: 510, y: 370, a: 15 }],
    };
  })(),

  // 2 – The rocket – the carrot rocket shoots the bunny over the big crater (new: rocket)
  (() => {
    const s1 = metal(170, 420, 300, 22, 15);
    return {
      id: 'rocket', par: 1, tools: { space_rocket: 1 }, learn: ['energy', 'direction'], intro: 'space_rocket', gravity: 0.45,
      statics: [s1, ground(300, 640, 600, 340), ground(780, 780, 360, 40), ground(1060, 690, 280, 220)],
      balls: [on(s1, -110, P)],
      goal: { type: 'bell', x: 1040, y: 530 },
      objs: [{ t: 'duck', x: 760, y: 360 }],
      solution: [{ t: 'space_rocket', x: 470, y: 440, a: 0 }],
    };
  })(),

  // 3 – The moon pulls – out in space the bunny flies straight on; a small moon pulls it to the rocket (new: moon)
  {
    id: 'moon-pulls', par: 1, tools: { space_moon: 1 }, learn: ['gravity', 'direction'], intro: 'space_moon', gravity: 0,
    statics: [metal(130, 300, 220, 22)],
    balls: [{ x: 60, y: 272, r: P, vx: 2.2 }],
    goal: { type: 'bell', x: 680, y: 390, beam: 90 },
    objs: [{ t: 'duck', x: 560, y: 160 }],
    solution: [{ t: 'space_moon', x: 690, y: 480 }],
  },

  // 4 – The asteroid – in space the asteroid floats still; the bunny nudges it and changes direction (new: asteroid)
  {
    id: 'asteroid', par: 1, tools: { space_asteroid: 1 }, learn: ['direction', 'weight'], intro: 'space_asteroid', gravity: 0,
    statics: [metal(130, 470, 220, 22)],
    balls: [{ x: 60, y: 442, r: P, vx: 3.5 }],
    goal: { type: 'bell', x: 700, y: 290, beam: 110 },
    objs: [{ t: 'duck', x: 1000, y: 440 }],
    solution: [{ t: 'space_asteroid', x: 640, y: 470 }],
    fails: [[{ t: 'space_asteroid', x: 640, y: 442 }]],
  },

  // 5 – Aim up – the rocket must point up at an angle so the bunny gets up onto the cliff
  (() => {
    const s1 = metal(150, 470, 260, 22, 16);
    return {
      id: 'aim-up', par: 1, tools: { space_rocket: 1 }, learn: ['direction', 'energy'], gravity: 0.45,
      statics: [s1, ground(330, 680, 660, 240), ground(930, 580, 540, 440), metal(1180, 300, 30, 160)],
      balls: [on(s1, -90, P)],
      goal: { type: 'bell', x: 1060, y: 310, beam: 70 },
      objs: [{ t: 'duck', x: 560, y: 220 }],
      solution: [{ t: 'space_rocket', x: 470, y: 520, a: -60 }],
      fails: [[{ t: 'space_rocket', x: 470, y: 535, a: 0 }], [{ t: 'space_rocket', x: 470, y: 520, a: -90 }]],
    };
  })(),

  // 6 – Not too close – the moon must be close enough to pull, but not so close that the bunny lands on it
  {
    id: 'not-too-close', par: 1, tools: { space_moon: 1 }, learn: ['gravity', 'direction'], gravity: 0,
    statics: [metal(120, 228, 200, 22)],
    balls: [{ x: 50, y: 200, r: P, vx: 2.2 }],
    goal: { type: 'bell', x: 820, y: 470, beam: 80 },
    objs: [{ t: 'duck', x: 300, y: 560 }],
    solution: [{ t: 'space_moon', x: 850, y: 400 }],
    fails: [[{ t: 'space_moon', x: 750, y: 290 }], [{ t: 'space_moon', x: 820, y: 700 }]],
  },

  // 7 – No air – the bunny floats straight on and keeps going; a tilted shield sends it down to the rocket (new: shield)
  {
    id: 'no-air', par: 1, tools: { space_shield: 1 }, learn: ['direction', 'energy'], intro: 'space_shield', gravity: 0,
    statics: [metal(110, 328, 180, 22), metal(560, 560, 30, 280), metal(1060, 700, 260, 22)],
    balls: [{ x: 50, y: 300, r: P, vx: 2.5 }],
    goal: { type: 'bell', x: 880, y: 600, beam: 80 },
    objs: [{ t: 'duck', x: 330, y: 560 }],
    solution: [{ t: 'space_shield', x: 880, y: 300, a: 45 }],
    fails: [[{ t: 'space_shield', x: 880, y: 300, a: -45 }], [{ t: 'space_shield', x: 880, y: 300, a: 0 }]],
  },

  // 8 – Round the planet – the rocket gives enough speed to swing round the big planet (the moon is a decoy)
  {
    id: 'around-the-planet', par: 1, tools: { space_rocket: 1, space_moon: 1 }, learn: ['gravity', 'energy', 'direction'], gravity: 0,
    statics: [metal(130, 560, 220, 22)],
    balls: [{ x: 60, y: 532, r: P, vx: 1.5 }],
    goal: { type: 'bell', x: 1000, y: 250, beam: 80 },
    objs: [{ t: 'space_planet', x: 640, y: 420, r: 70, R: 230, G: 5000, col: ['#FF9FB8', '#E05A80'] }, { t: 'duck', x: 640, y: 150 }],
    solution: [{ t: 'space_rocket', x: 330, y: 532, a: 0 }],
    fails: [[{ t: 'space_rocket', x: 330, y: 532, a: 15 }], [{ t: 'space_moon', x: 330, y: 380 }]],
  },

  // 9 – Moon trampoline – on the moon a small bounce sends the bunny high and far, over the wall (the plank is a decoy)
  (() => {
    const s1 = metal(130, 200, 220, 22, 10);
    return {
      id: 'moon-trampoline', par: 1, tools: { pad: 1, plank: 1 }, learn: ['spring', 'gravity'], gravity: 0.45,
      statics: [s1, ground(300, 760, 600, 80), metal(600, 560, 30, 340), ground(950, 760, 500, 80), metal(1170, 560, 30, 340)],
      balls: [on(s1, -70, P)],
      goal: { type: 'bowl', x: 1010, y: 720, w: 240 },
      objs: [{ t: 'duck', x: 600, y: 240 }],
      solution: [{ t: 'pad', x: 420, y: 560, a: 0 }],
      fails: [[{ t: 'pad', x: 420, y: 560, a: -30 }], [{ t: 'plank', x: 420, y: 500, a: 15 }]],
    };
  })(),

  // 10 – The seesaw – the moon rock falls slowly onto the seesaw and throws the bunny high up (the plank is a decoy)
  {
    id: 'seesaw', par: 1, tools: { stone: 1, plank: 1 }, learn: ['lever', 'gravity'], gravity: 0.45,
    statics: [metal(522, 585, 10, 40), ground(400, 720, 120, 160), ground(1000, 760, 400, 80)],
    fixed: [{ t: 'seesaw', x: 400, y: 560, a: 20 }],
    balls: [{ x: 495, y: 570, r: P }],
    goal: { type: 'bell', x: 478, y: 280, beam: 60 },
    objs: [{ t: 'duck', x: 900, y: 300 }],
    solution: [{ t: 'stone', x: 310, y: 250 }],
    fails: [[{ t: 'stone', x: 480, y: 250 }], [{ t: 'plank', x: 300, y: 300, a: 30 }]],
  },

  // 11 – The button – on the moon the asteroid falls slowly onto the button and the hatch opens (the plank is a decoy)
  (() => {
    const s1 = metal(220, 380, 420, 22, 8);
    return {
      id: 'button', par: 1, tools: { space_asteroid: 1, plank: 1 }, learn: ['gravity', 'chain_reaction'], gravity: 0.45,
      statics: [s1, metal(600, 420, 300, 22), ground(900, 760, 600, 80), metal(560, 180, 160, 20)],
      balls: [on(s1, -170, P)],
      goal: { type: 'bowl', x: 900, y: 720, w: 200 },
      objs: [{ t: 'space_hatch', x: 740, y: 340, w: 22, h: 140, id: 'g1' }, { t: 'button', x: 560, y: 160, act: { fire: 'g1' } }, { t: 'duck', x: 1000, y: 300 }],
      solution: [{ t: 'space_asteroid', x: 560, y: 60 }],
      fails: [[{ t: 'space_asteroid', x: 400, y: 60 }], [{ t: 'plank', x: 820, y: 500, a: 15 }]],
    };
  })(),

  // 12 – Over the wall – the rocket sends the bunny up over the wall, the shield sends it down into the hatch (two steps; the moon is a decoy)
  {
    id: 'over-the-wall', par: 2, tools: { space_rocket: 1, space_shield: 1, space_moon: 1 }, learn: ['direction', 'energy'], gravity: 0,
    statics: [metal(120, 628, 200, 22), metal(700, 530, 30, 380)],
    balls: [{ x: 50, y: 600, r: P, vx: 1.6 }],
    goal: { type: 'bell', x: 900, y: 580, beam: 70 },
    objs: [{ t: 'duck', x: 520, y: 700 }],
    solution: [{ t: 'space_rocket', x: 300, y: 600, a: -45 }, { t: 'space_shield', x: 800, y: 110, a: 15 }],
    fails: [[{ t: 'space_rocket', x: 300, y: 600, a: -45 }], [{ t: 'space_shield', x: 800, y: 110, a: 15 }], [{ t: 'space_rocket', x: 300, y: 600, a: -45 }, { t: 'space_moon', x: 1000, y: 250 }]],
  },

  // 13 – Two rockets – the first shoots the bunny up onto the shelf, the second over the crater (the plank is a decoy)
  (() => {
    const s1 = metal(140, 640, 240, 22, 10);
    return {
      id: 'two-rockets', par: 2, tools: { space_rocket: 2, plank: 1 }, learn: ['direction', 'energy', 'gravity'], gravity: 0.45,
      statics: [s1, ground(290, 730, 580, 100), ground(690, 610, 220, 380), metal(690, 410, 240, 22, -6), metal(586, 380, 14, 50), metal(800, 290, 26, 250), ground(1080, 600, 240, 400), metal(1188, 250, 24, 300)],
      balls: [on(s1, -100, P)],
      goal: { type: 'bell', x: 1110, y: 350, beam: 60 },
      objs: [{ t: 'duck', x: 900, y: 250 }],
      solution: [{ t: 'space_rocket', x: 400, y: 645, a: -75 }, { t: 'space_rocket', x: 660, y: 385, a: -75 }],
      fails: [[{ t: 'space_rocket', x: 400, y: 645, a: -75 }], [{ t: 'space_rocket', x: 660, y: 385, a: -75 }], [{ t: 'space_rocket', x: 400, y: 645, a: -75 }, { t: 'space_rocket', x: 640, y: 385, a: 0 }]],
    };
  })(),

  // 14 – Hop and hatch – combine: the trampoline sends the bunny high over the wall, the asteroid falls on the button and opens the hatch (the rocket is a decoy)
  (() => {
    const s1 = metal(130, 200, 220, 22, 10);
    return {
      id: 'hop-and-hatch', par: 2, tools: { pad: 1, space_asteroid: 1, space_rocket: 1 }, learn: ['spring', 'gravity', 'chain_reaction'], gravity: 0.45,
      statics: [s1, ground(300, 760, 600, 80), metal(600, 480, 30, 560), ground(900, 770, 600, 60), metal(1185, 560, 30, 340),
        metal(1080, 220, 160, 20)],
      balls: [on(s1, -70, P)],
      goal: { type: 'bowl', x: 1010, y: 740, w: 240 },
      objs: [{ t: 'space_hatch', x: 850, y: 250, w: 22, h: 300, id: 'g1' }, { t: 'button', x: 1080, y: 200, act: { fire: 'g1' } }, { t: 'duck', x: 760, y: 600 }],
      solution: [{ t: 'pad', x: 420, y: 560, a: 0 }, { t: 'space_asteroid', x: 1080, y: 110 }],
      fails: [[{ t: 'pad', x: 420, y: 560, a: 0 }], [{ t: 'space_asteroid', x: 1080, y: 110 }], [{ t: 'pad', x: 420, y: 560, a: 0 }, { t: 'space_rocket', x: 1080, y: 110, a: 90 }]],
    };
  })(),

  // 15 – Master – the rocket sends the bunny up, two shields guide it round the planet and down into the hatch (moon and asteroid are decoys)
  {
    id: 'master', par: 3, tools: { space_rocket: 1, space_shield: 2, space_moon: 1, space_asteroid: 1 }, learn: ['direction', 'energy', 'gravity'], gravity: 0, time: 26,
    statics: [metal(120, 708, 200, 22), metal(520, 560, 30, 400), metal(800, 620, 30, 280)],
    balls: [{ x: 50, y: 680, r: P, vx: 1.5 }],
    goal: { type: 'bell', x: 960, y: 640, beam: 70 },
    objs: [{ t: 'space_planet', x: 700, y: 330, r: 55, R: 170, G: 3000, col: ['#9FE6C0', '#3FB57E'] }, { t: 'duck', x: 330, y: 400 }],
    solution: [{ t: 'space_rocket', x: 330, y: 680, a: -90 }, { t: 'space_shield', x: 330, y: 110, a: -45 }, { t: 'space_shield', x: 960, y: 110, a: 45 }],
    fails: [[{ t: 'space_rocket', x: 330, y: 680, a: -90 }, { t: 'space_shield', x: 330, y: 110, a: -45 }],
      [{ t: 'space_rocket', x: 330, y: 680, a: -90 }, { t: 'space_shield', x: 330, y: 110, a: 45 }, { t: 'space_shield', x: 960, y: 110, a: 45 }],
      [{ t: 'space_rocket', x: 330, y: 680, a: -90 }, { t: 'space_shield', x: 330, y: 110, a: -45 }, { t: 'space_moon', x: 960, y: 450 }]],
  },
]);
